import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Form, Field } from 'react-final-form';
import { Image, Flex } from 'mdlkit';
import IconButton from './IconButton';
import { Fade } from '../../../core/animations';
import { required } from '../../../utils/shared/validations';
import { SMS, PHONE, EMAIL } from '../../../constants/login';
// import { REGISTRATION } from '../../../constants/events';
import {
  AuthCode,
  AuthCodeResponse,
  ConnectionType,
} from '../../../interfaces/login';
import toast from '../../../utils/toast';

const ButtonWrapper = styled(Flex)`
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
  }
`;

const StyledIconButton = styled(IconButton)`
  flex: 1;
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.boxShadows[1]};
  overflow: hidden;

  &:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #000000 !important;
    outline: none !important;
  }

  &:nth-child(2) {
    margin: 20px 0px;
  }

  img {
    height: 45px;
    margin-bottom: 5px;
  }

  p {
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:nth-child(2) {
      margin: 0px 20px;
    }
  }
`;

interface Props {
  isOpen: boolean;
  authCodeRes: AuthCodeResponse;
  onResendCode: (values: AuthCode) => void;
}

const PasswordRecoveryResendCodeForm: FunctionComponent<Props> = ({
  isOpen,
  authCodeRes,
  onResendCode,
}: Props) => {
  const { formatMessage: f } = useIntl();

  const handleClick = (form, connectionType: ConnectionType) => (e) => {
    e.preventDefault();

    window.createPageActionEvent({
      controlName: 'resend-code-account-link',
      controlText: 'Resend code',
      additionalInfo: [{ key: 'verifyType', value: connectionType }],
    });

    toast.dismiss();
    form.change('connection', connectionType);
    form.submit();
  };

  const {
    user: { id, email, phone },
    connection,
  } = authCodeRes;

  const options = [
    {
      id: 'resendCode',
      icon: <Image src="/static/svg/resendSms.svg" />,
      label: f({ id: 'passwordRecovery.resendCode' }),
      placeholder: f({ id: 'passwordRecovery.getNewCode' }),
      connectionType: SMS,
    },
    {
      id: 'phoneCall',
      icon: <Image src="/static/svg/phone.svg" />,
      label: f({ id: 'passwordRecovery.phoneCall' }),
      placeholder: phone,
      connectionType: PHONE,
    },
    {
      id: 'email',
      icon: <Image src="/static/svg/email.svg" />,
      label: f({ id: 'email' }),
      placeholder: email,
      connectionType: EMAIL,
    },
  ];
  return (
    <Fade in={isOpen}>
      <Form
        onSubmit={onResendCode}
        initialValues={{ userId: id, connection }}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <Field
              id="userId"
              name="userId"
              type="hidden"
              validate={required}
              component="input"
            />
            <Field
              id="connectionType"
              name="connection"
              type="hidden"
              validate={required}
              component="input"
            />
            <ButtonWrapper mt="xs">
              {options.map(
                ({ id: optId, icon, label, placeholder, connectionType }) => (
                  <StyledIconButton
                    key={optId}
                    id={optId}
                    icon={icon}
                    label={label}
                    placeholder={placeholder as string}
                    onClick={handleClick(
                      form,
                      connectionType as ConnectionType
                    )}
                  />
                )
              )}
            </ButtonWrapper>
          </form>
        )}
      />
    </Fade>
  );
};

export default PasswordRecoveryResendCodeForm;
